import {
  Appointment,
  BookingData,
  ApiResponse,
  ServiceType,
  Service,
  SubService,
} from "../types";
import { apiClient } from "./apiClient";
import { Company } from "./companyApi";
import { getCompanyNicknameFromHost } from "../utils/subdomain";

export const publicApi = {
  // Get company information (public)
  async getCompany(): Promise<ApiResponse<Company>> {
    try {
      const nickname = getCompanyNicknameFromHost();
      const query = nickname ? `?nickname=${encodeURIComponent(nickname)}` : "";
      const data = await apiClient.get<any>(
        `/public/company${query}`,
        { requiresAuth: false }
      );

      return {
        success: true,
        data: data.data || data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Book an appointment (public)
  async bookAppointment(
    bookingData: BookingData
  ): Promise<ApiResponse<Appointment>> {
    try {
      // Transform BookingData to the required API format
      const requestBody: any = {
        professionalId: bookingData.professionalId,
        serviceId: bookingData.serviceId,
        appointmentDate: bookingData.date,
        phoneNumber: bookingData.phoneNumber,
        clientId: bookingData.clientId,
        clientName: bookingData.clientName, // For new client creation
        birthday: bookingData.birthday, // For new client creation
        subServiceIds: bookingData.subServiceIds || [],
      };

      // Only include startTime if service type is TIME
      if (
        bookingData.serviceType === ServiceType.TIME &&
        bookingData.startTime
      ) {
        requestBody.startTime = bookingData.startTime;
      }

      const data = await apiClient.post<ApiResponse<Appointment>>(
        "/public/appointments",
        requestBody,
        { requiresAuth: false }
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error: any) {
      console.error("Error booking appointment:", error);
      return {
        success: false,
        error: error.message || "Failed to book appointment",
      };
    }
  },

  // Get appointments by phone number (public)
  async getAppointmentsByPhoneNumber(
    phoneNumber: string,
  ): Promise<ApiResponse<Appointment[]>> {
    try {
      const data = await apiClient.get<{ data: Appointment[] }>(
        `/public/appointments?phoneNumber=${phoneNumber}`,
        { requiresAuth: false }
      );

      return {
        success: true,
        data: data.data || [],
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  },

  // Reschedule an appointment (public)
  async rescheduleAppointment(
    appointmentId: number,
    newDate: string,
    newTime?: string
  ): Promise<ApiResponse<Appointment>> {
    try {
      const requestBody: any = {
        appointmentDate: newDate,
      };

      // Only include startTime if provided
      if (newTime) {
        requestBody.startTime = newTime;
      }

      const data = await apiClient.put<{ data: Appointment }>(
        `/public/appointments/${appointmentId}`,
        requestBody,
        { requiresAuth: false }
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to reschedule appointment",
      };
    }
  },

  // Cancel an appointment (public)
  async cancelAppointment(
    appointmentId: number
  ): Promise<ApiResponse<Appointment>> {
    try {
      const data = await apiClient.patch<{ data: Appointment }>(
        `/public/appointments/${appointmentId}/cancel`,
        undefined,
        { requiresAuth: false }
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to cancel appointment",
      };
    }
  },

  // Get available slots by professional (public)
  async getAvailableSlotsByProfessional(
    professionalId: number,
    serviceId: number,
    date?: string
  ): Promise<ApiResponse<any[]>> {
    try {
      let endpoint = `/public/slots/professional/${professionalId}/service/${serviceId}`;
      if (date) {
        endpoint += `?date=${date}`;
      }

      const data = await apiClient.get<any>(endpoint, {
        requiresAuth: false,
      });

      return {
        success: true,
        data: data.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Get available slots by company (public)
  async getAvailableSlotsByCompany(
    serviceId: number,
    date?: string
  ): Promise<ApiResponse<any[]>> {
    try {
      let endpoint = `/public/slots/service/${serviceId}`;
      if (date) {
        endpoint += `?date=${date}`;
      }

      const data = await apiClient.get<any>(endpoint, {
        requiresAuth: false,
      });

      return {
        success: true,
        data: data.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Get services by company (public)
  async getServicesByCompany(): Promise<ApiResponse<Service[]>> {
    try {
      const data = await apiClient.get<{ data: Service[] } | Service[]>(
        `/public/services`,
        { requiresAuth: false }
      );

      // Handle both response formats (with or without data wrapper)
      const services = Array.isArray(data) ? data : (data as any).data || data;

      return {
        success: true,
        data: services,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  },

  // Get subservices by company (public)
  async getSubServicesByCompany(): Promise<ApiResponse<SubService[]>> {
    try {
      const data = await apiClient.get<any>(`/public/subservices`, {
        requiresAuth: false,
      });

      return {
        success: true,
        data: data.data || data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },
};
